// src/components/pages/JobCandidates.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import storageService from '../../services/storageService';
import { APPLICATION_STATUS } from '../../constants/statusConstants';
import './JobCandidates.css';

const JobCandidates = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [company, setCompany] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJobData();
  }, [jobId]);

  const loadJobData = () => {
    const jobData = storageService.getJob(jobId);
    const companyData = jobData ? storageService.getCompany(jobData.companyId) : null;
    
    if (jobData && companyData) {
      // Get all placements for this job
      const allPlacements = storageService.getPlacementsByJob(jobId);
      
      // Get candidate details for each placement
      const placementsWithDetails = allPlacements.map(placement => {
        const candidate = storageService.getCandidate(placement.candidateId);
        return {
          ...placement,
          candidateName: candidate?.name || 'Unknown',
          candidateEmail: candidate?.email || '',
          candidatePhone: candidate?.phone || '',
          candidateSkills: candidate?.skills || '',
          candidateExperience: candidate?.experience || ''
        };
      });
      
      setJob(jobData);
      setCompany(companyData);
      setPlacements(placementsWithDetails);
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading job candidates...</p>
      </div>
    );
  }

  if (!job || !company) {
    return (
      <div className="error-state">
        <h2>Job not found</h2>
        <Link to="/jobs" className="btn-primary">
          Back to Jobs
        </Link>
      </div>
    );
  }

  // Helper function for status colors
  const getStatusClass = (status) => {
    switch(status) {
      case APPLICATION_STATUS.RESUME_SENT: return 'resume-sent';
      case APPLICATION_STATUS.INTERVIEW_IN_PROGRESS: return 'interview';
      case APPLICATION_STATUS.SELECTED: return 'selected';
      case APPLICATION_STATUS.JOINED: return 'joined';
      case APPLICATION_STATUS.REJECTED: return 'rejected';
      default: return 'default';
    }
  };

  return (
    <div className="job-candidates-page">
      {/* Job Header */}
      <div className="job-header-section">
        <div className="job-header-top">
          <Link to={`/company/${company.id}/jobs`} className="back-link">
            ← Back to {company.name} Jobs
          </Link>
          <Link 
            to={`/placements/new?jobId=${jobId}`}
            className="btn-primary"
          >
            + Add Candidate to Job
          </Link>
        </div>
        
        <div className="job-detail-card">
          <div className="job-header">
            <div>
              <h1>{job.title}</h1>
              <div className="job-meta">
                <Link to={`/company/${company.id}/jobs`} className="company-link">
                  {company.name}
                </Link>
                <span className="job-location">{job.location || 'Remote'}</span>
                <span className="job-type">{job.jobType}</span>
              </div>
            </div>
            <div className="job-stats">
              <div className="stat-item">
                <span className="stat-value">{placements.length}</span>
                <span className="stat-label">Candidates</span>
              </div>
              <div className="stat-item">
                <span className={`status-badge ${job.status === 'Open' ? 'open' : 'closed'}`}>
                  {job.status}
                </span>
              </div>
            </div>
          </div>
          
          <div className="job-details">
            <div className="detail-row">
              <div className="detail-item">
                <span className="detail-label">Experience:</span>
                <span className="detail-value">{job.experience}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Salary:</span>
                <span className="detail-value">{job.salary}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Posted:</span>
                <span className="detail-value">{job.createdAt}</span>
              </div>
            </div>
            
            <div className="detail-row">
              <div className="detail-item">
                <span className="detail-label">Requirements:</span>
                <span className="detail-value">{job.requirements}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Candidates Table */}
      <div className="section-container">
        <div className="section-header">
          <h2>Candidates ({placements.length})</h2>
          <div className="status-filters">
            {Object.values(APPLICATION_STATUS).map(status => (
              <button
                key={status}
                className="status-filter-btn"
              >
                {status} ({placements.filter(p => p.status === status).length})
              </button>
            ))}
          </div>
        </div>
        
        <div className="candidates-table-container">
          {placements.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Skills</th>
                  <th>Experience</th>
                  <th>Applied Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {placements.map(placement => (
                  <tr key={placement.id}>
                    <td>
                      <div className="candidate-cell">
                        <div className="candidate-avatar">
                          {placement.candidateName.charAt(0)}
                        </div>
                        <div className="candidate-info">
                          <span className="candidate-name">{placement.candidateName}</span>
                          <div className="candidate-contact">
                            <a href={`mailto:${placement.candidateEmail}`} className="email-link">
                              {placement.candidateEmail}
                            </a>
                            <span className="candidate-phone">{placement.candidatePhone}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="skills-cell">
                        {placement.candidateSkills?.split(',').slice(0, 3).map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill.trim()}</span>
                        ))}
                        {placement.candidateSkills?.split(',').length > 3 && (
                          <span className="more-tag">+{placement.candidateSkills.split(',').length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="experience-badge">{placement.candidateExperience}</span>
                    </td>
                    <td>{placement.appliedDate}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(placement.status)}`}>
                        {placement.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/placement/${placement.id}`}
                          className="btn-view-details"
                        >
                          View Details
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">👤</div>
              <h3>No candidates applied for this job</h3>
              <p>Add candidates to start tracking applications</p>
              <Link 
                to={`/placements/new?jobId=${jobId}`}
                className="btn-primary"
              >
                + Add Candidate
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCandidates;